import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { catchError, of, tap } from 'rxjs';
import { Extern } from 'src/app/models/extern.model';
import { ExternService } from 'src/app/services/extern.service';
import { CssThemeService } from 'src/app/services/css-theme.service';
import { ANIMAL_TYPES } from 'src/app/constants/animal-types.constants';

@Component({
    selector: 'app-edit-extern',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.scss'],
    standalone: false
})
export class EditExternComponent implements OnInit {

	public loading = false;
	public editForm: UntypedFormGroup;
    public selectedFile: File;
	@Input('extern') extern: Extern | null;
	public errorText = '';
	public cssThemes: string[];
	public animalTypes = ANIMAL_TYPES;
	public selectedAnimalTypeIds = new Set<number>();

	public exportTypes = [
		{ label: 'FTP', value: 'FTP' },
		{ label: 'API', value: 'API' },
	];
	public coopDisplayModes = [
		{ label: 'Complet', value: 'FULL' },
		{ label: 'Limité', value: 'LIGHT' }
	];

	constructor(
		protected dialogRef: NbDialogRef<EditExternComponent>,
		protected formBuilder: UntypedFormBuilder,
		protected externService: ExternService,
		private customThemeService: CssThemeService
	) { }

	ngOnInit(): void {

		this.editForm = this.formBuilder.group({
			isCoop: [false, Validators.required],
			name: [null, Validators.required],
			cssTheme: ['qwintal', Validators.required],
			exportType: [null, Validators.required],
			ftpPath: [null],
			ftpFileFormat: [null],
			expirationDate: [null],
			coopDisplayMode: ['FULL', Validators.required],
		});
		this.cssThemes = this.customThemeService.getThemes();

		if (this.extern) {
			this.editForm.patchValue(this.extern);
			this.selectedAnimalTypeIds = new Set(this.extern.animalTypeIds ?? []);
		}

		this.toggleCoopFields(this.editForm.get('isCoop')?.value);
		this.editForm.get('isCoop')?.valueChanges.subscribe(isCoop => this.toggleCoopFields(isCoop));
	}

	// Thème et mode d'affichage ne concernent que les coops : désactivés, ils ne bloquent plus la
	// validation (css_theme est NULL pour certains externes). Leur valeur reste envoyée à l'API.
	private toggleCoopFields(isCoop: boolean): void {
		for (const name of ['cssTheme', 'coopDisplayMode']) {
			const control = this.editForm.get(name);
			if (isCoop) {
				control?.enable({ emitEvent: false });
			} else {
				control?.disable({ emitEvent: false });
			}
		}
	}

	public isAnimalTypeSelected(typeId: number): boolean {
		return this.selectedAnimalTypeIds.has(typeId);
	}

	public onAnimalTypeToggle(typeId: number, isChecked: boolean): void {
		if (isChecked) {
			this.selectedAnimalTypeIds.add(typeId);
		} else {
			this.selectedAnimalTypeIds.delete(typeId);
		}
	}

	public isFormValid(): boolean {
		return this.editForm.valid && this.selectedAnimalTypeIds.size > 0;
	}

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input && input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
        }
    }
	
	public saveAction(): void {

		this.loading = true;

        const formData: FormData = new FormData();
        formData.append('name', this.editForm.get('name')?.value || '');
        formData.append('isCoop', this.editForm.get('isCoop')?.value || '');
		formData.append('cssTheme', this.editForm.get('cssTheme')?.value || '');
        formData.append('exportType', this.editForm.get('exportType')?.value || '');
		formData.append('ftpPath', this.editForm.get('ftpPath')?.value || '');
		formData.append('ftpFileFormat', this.editForm.get('ftpFileFormat')?.value || '');
        formData.append('expirationDate', this.editForm.get('expirationDate')?.value || '');
        formData.append('coopDisplayMode', this.editForm.get('coopDisplayMode')?.value || '');
		formData.append('animalTypeIds', JSON.stringify(Array.from(this.selectedAnimalTypeIds).sort((a, b) => a - b)));

        if (this.selectedFile) {
            formData.append('logo', this.selectedFile, this.selectedFile.name);
        }

		if (this.extern) {

			this.externService.update(this.extern.id, formData).pipe(
				catchError((error, caught) => {
					this.loading = false;
					return of(null);
				}),
				tap(extern => {

					this.loading = false;
					if (!extern) { return; }
					this.dialogRef.close({ id: extern.id });
				}),
			).subscribe();

		} else {
			this.externService.add(formData).pipe(
				catchError((error, caught) => {
					this.loading = false;
					return of(null);
				}),
				tap(extern => {

					this.loading = false;
					if (!extern) { return; }
					this.dialogRef.close({ id: extern.id });
				}),
			).subscribe();
		}
	}

	public closeAction(): void {
		this.dialogRef.close();
	}
}
